/**
 * AI Actions Configuration
 *
 * Data-driven configuration for AI text actions.
 * Each action maps to a prompt and optional parameters.
 *
 * threadId = sessionId (always, no fallbacks!)
 */

import { BRAND_DISPLAY_NAME } from '@streamlain/brand';

import { Endpoint } from './copilot-client';
import type { PromptKey } from './prompt';

export interface AIActionConfig {
  prompt: PromptKey;
  endpoint?: Endpoint;
  timeout?: number;
  /**
   * Map action options to context parameters.
   * Returns partial context object to merge with defaults.
   */
  mapContext?: (options: any) => Record<string, unknown>;
}

/**
 * Simple text-to-text actions.
 * All use the same pattern: prompt + input → textToText
 */
export const TEXT_ACTIONS: Record<string, AIActionConfig> = {
  // Writing improvements
  summary: { prompt: 'Summary' },
  improveWriting: { prompt: 'Improve writing for it' },
  improveGrammar: { prompt: 'Improve grammar for it' },
  fixSpelling: { prompt: 'Fix spelling for it' },
  createHeadings: { prompt: 'Create headings' },
  makeLonger: { prompt: 'Make it longer' },
  makeShorter: { prompt: 'Make it shorter' },
  continueWriting: { prompt: 'Continue writing' },

  // Code
  checkCodeErrors: { prompt: 'Check code error' },
  explainCode: { prompt: 'Explain this code' },

  // Content creation
  writeArticle: { prompt: 'Write an article about this' },
  writeTwitterPost: { prompt: 'Write a twitter about this' },
  writePoem: { prompt: 'Write a poem about this' },
  writeOutline: { prompt: 'Write outline' },
  writeBlogPost: { prompt: 'Write a blog post about this' },
  brainstorm: { prompt: 'Brainstorm ideas about this' },
  findActions: { prompt: 'Find action items from it' },

  // Explanation
  explain: { prompt: 'Explain this' },
  explainImage: { prompt: 'Explain this image' },
  generateCaption: { prompt: 'Generate a caption' },
};

/**
 * Actions with additional parameters mapped from options.
 */
export const PARAMETERIZED_ACTIONS: Record<string, AIActionConfig> = {
  translate: {
    prompt: 'Translate to',
    mapContext: (opts: { lang?: string }) => ({
      language: opts.lang,
    }),
  },
  changeTone: {
    prompt: 'Change tone to',
    mapContext: (opts: { tone?: string }) => ({
      tone: opts.tone?.toLowerCase(),
    }),
  },
  expandMindmap: {
    prompt: 'Expand mind map',
    mapContext: (opts: { mindmap?: string; input?: string }) => ({
      mindmap: opts.mindmap,
      node: opts.input,
    }),
  },
};

/**
 * Actions with custom endpoint or timeout.
 */
export const WORKFLOW_ACTIONS: Record<string, AIActionConfig> = {
  brainstormMindmap: {
    prompt: 'workflow:brainstorm',
    endpoint: Endpoint.Workflow,
    timeout: 180000, // 3 minutes
  },
};

/**
 * Chat action - special handling for contexts and webSearch.
 * Registered separately due to complex context mapping.
 */
export const CHAT_ACTION: AIActionConfig = {
  prompt: `Chat With ${BRAND_DISPLAY_NAME} AI`,
  endpoint: Endpoint.StreamObject,
  timeout: 5 * 60 * 1000, // 5 minutes
};

/**
 * All text actions combined for iteration.
 */
export const ALL_TEXT_ACTIONS: Record<string, AIActionConfig> = {
  ...TEXT_ACTIONS,
  ...PARAMETERIZED_ACTIONS,
  ...WORKFLOW_ACTIONS,
};
